import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/App/redux/hooks';
import Authentication from 'src/App/Services/Authentication.service';
import { ICommand } from 'src/App/Types/command.types';
import { CommandItem } from './CommandItem/CommandItem';
import { CommandsContain, CommandsList } from './Commands.styles';
import { toast } from 'react-toastify';
import { addCooldown, checkCooldowns, cooldownWentThrough } from 'src/App/redux/slices/App.slice';
import { HfxItem } from './HfxItem/HfxItem';
import { RootState } from 'src/App/redux/store';
import Swal from 'sweetalert2';
import { TTransaction } from 'src/App/Types/twitch.types';

const baseSku = 'lumia-bits-';

export function Commands({ type, changeTitle }: { type: 'commands' | 'hfx'; changeTitle: (title: string) => void }) {
	const dispatch = useAppDispatch();
	const pendingPromiseRef = useRef<any>();
	const user_id = useAppSelector((state: RootState) => state.app.user_id);
	const extensions_id = useAppSelector((state: RootState) => state.app.extensions_id);
	const commands = useAppSelector((state) => state.app.commands.filter((command) => (type === 'hfx' ? !!command.hfx : !command.hfx)));
	const [transaction, setTransaction] = useState<TTransaction | null>(null);
	const [processing, setProcessing] = useState<ICommand | null>(null);
	const [sendProcessedCommand, setSendProcessedCommand] = useState<boolean | number>(false);
	const [error, setError] = useState<string>('');
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		setupCommandListeners();
		changeTitle("Take control of your favorite streamer's setup");
		return () => {
			pendingPromiseRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (sendProcessedCommand) {
			sendCommand();
		}
	}, [sendProcessedCommand]);

	const startTimeout = useCallback(
		({ id, time }) => {
			dispatch(addCooldown({ id, time }));
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			// Will check all cooldowns after time + 300 miliseconds just to give it a small buffer
			timeoutRef.current = setTimeout(() => {
				dispatch(checkCooldowns());
			}, time + 300);
		},
		[dispatch],
	);

	function setupCommandListeners() {
		const twitch = globalThis.Twitch ? globalThis.Twitch.ext : null;

		if (twitch) {
			// When in development this simulates a live service usage for bits that will simulary user approval after a fixed delay
			twitch.bits.setUseLoopback = true;
			// After a bit purchase was made
			twitch.bits.onTransactionComplete((transaction: TTransaction) => {
				console.debug('onTransactionComplete: ', transaction);
				// All transaction completes get sent here. So we need to make sure it's for the current user
				if (transaction.initiator === 'current_user') {
					setTransaction(transaction);
					setSendProcessedCommand(true);
				}
			});

			// After a bit purchase has failed
			twitch.bits.onTransactionCancelled((transaction) => {
				console.debug('onTransactionCancelled: ', transaction);
				pendingPromiseRef.current?.reject('Bits redeem cancelled');
				setTransaction(null);
				cleanup(true);
			});
		} else {
			pendingPromiseRef.current?.reject('Twitch Extension not loaded');
			cleanup(true);
		}
	}

	const cleanup = useCallback(
		(cleanupError = false) => {
			setProcessing(null);
			setTransaction(null);
			setSendProcessedCommand(false);
			pendingPromiseRef.current = null;
			if (cleanupError) {
				setError('');
			}
		},
		[setProcessing, setSendProcessedCommand, setError],
	);

	// Process Bits and Free commands
	const handleCommand = useCallback(
		(command): Promise<boolean> => {
			return new Promise(async (resolve, reject) => {
				try {
					cleanup(true);
					setError('');
					const twitch = globalThis.Twitch ? globalThis.Twitch.ext : null;
					let free_ticket_id: number | null = null;

					setProcessing(command);
					const isBroadcaster = !!Authentication.isBroadcaster();

					try {
						const checkRes = await Authentication.checkCommandCall({ user_id, extensions_id, command_id: command.id || command.name, command_name: command.name, amount: command.bits });
						if (!isBroadcaster && command.bits && checkRes?.data?.free_ticket_id && command.bits <= checkRes.data?.free_ticket_amount) {
							const swalResponse = (
								await Swal.fire({
									title: 'Free Ticket Available',
									text: `You have a free ticket available in the amount of ${checkRes.data.free_ticket_amount} bits. Would you like to use it now?`,
									showCancelButton: true,
									confirmButtonText: 'Yes, use free ticket',
									cancelButtonText: 'No, use bits',
								})
							).value;
							if (swalResponse) {
								free_ticket_id = checkRes.data.free_ticket_id;
							}
						}

						dispatch(cooldownWentThrough({ id: command.name }));
					} catch (err: any) {
						console.error('Processing err: ', err);
						cleanup();
						if (err?.response?.data?.data?.cooldown_key) {
							if (err.response.data.data.cooldown_key === 'cooldown-3' || err.response.data.data.cooldown_key === 'cooldown-command') {
								toast.error(
									`${err.response.data.chatbotMessage ?? 'Command is currently in a cooldown'}. There are ${(err.response.data.data.cooldown_time_left / 1000).toFixed(1)} seconds remaining`,
								);
								startTimeout({ id: command.name, time: err.response.data.data.cooldown_time_left });
							} else {
								toast.error(
									`${err.response.data.chatbotMessage ?? 'Channel is currently in a cooldown'}. There are ${(err.response.data.data.cooldown_time_left / 1000).toFixed(1)} seconds remaining`,
								);
								startTimeout({ id: 'channel', time: err.response.data.data.cooldown_time_left });
							}
						} else if (err?.response?.data === 'offline') {
							toast.error('Stream is currently offline');
						}
						return reject(err);
					}

					// Check if streamer is on their own channel
					if (free_ticket_id) {
						if (command.bits) {
							toast.info('Free Ticket has been used');
						}

						pendingPromiseRef.current = { resolve, reject };
						setSendProcessedCommand(free_ticket_id);
					} else if (!isBroadcaster && command.bits) {
						const sku = `${baseSku}${command.bits}`;
						if (twitch) {
							pendingPromiseRef.current = { resolve, reject };
							try {
								twitch.bits.useBits(sku);
								twitch.bits.showBitsBalance();
							} catch (err: any) {
								cleanup();
								console.error('Use bits err: ', err);
								return reject(err);
							}
						} else {
							reject('Twitch Extension not loaded');
							cleanup(true);
						}
					} else {
						if (isBroadcaster && command.bits) {
							toast.warn('Broadcasters can not use Bits on their own channel, so this will not take any bits');
						}

						pendingPromiseRef.current = { resolve, reject };
						setSendProcessedCommand(true);
					}
				} catch (err) {
					console.error('Handle command err: ', err);
					cleanup();
					return reject(err);
				}
			});
		},
		[user_id, cleanup, dispatch, setError, setProcessing, startTimeout, setSendProcessedCommand],
	);

	// After bits has been process or if it's a free submission
	async function sendCommand() {
		const command = { ...processing };
		if (!command) {
			pendingPromiseRef.current?.reject('Queue empty');
			cleanup(true);
			return;
		}

		try {
			await Authentication.commandCall({
				user_id,
				extensions_id,
				command_name: command.name,
				command_id: command.id || command.name,
				command_cooldown_on: command.cooldown?.on,
				command_cooldown_message: command.cooldown?.message,
				command_cooldown_duration: command.cooldown?.duration,
				command_cooldown_type: 'channel',
				bits: command.bits,
				prompt: command.prompt,
				is_hudfx: command.hfxOrigin === 'hudfx',
				hudfx_id: command.hfx,
				free_ticket_id: typeof sendProcessedCommand === 'number' ? (sendProcessedCommand as number) : null,
				transactionId: transaction?.transactionId,
				transactionReceipt: transaction?.transactionReceipt,
			});
			pendingPromiseRef.current?.resolve(true);
			cleanup(true);
		} catch (err: any) {
			console.error('Error sending command: ', err);
			if (err && err.request && err.request.status && err.request.response) {
				pendingPromiseRef.current?.reject(`Ran into ${err.request.status} error sending command: ${err.request.response}`);
			} else {
				pendingPromiseRef.current?.reject('Ran into an error sending command');
			}
			cleanup();
		}
	}

	return (
		<CommandsContain>
			{error && <div>{error}</div>}
			<CommandsList>
				{commands.map((command) =>
					type === 'hfx' ? <HfxItem key={command.name} command={command} triggerCommand={handleCommand} /> : <CommandItem key={command.name} triggerCommand={handleCommand} command={command} />,
				)}
			</CommandsList>
		</CommandsContain>
	);
}
